import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { getUserRole } from '../services/userService';
import { initializeRemoteConfig, getRemoteConfigValue } from '../services/remoteConfig';
import { auth } from '../config/firebase';
import Loader from '../components/Loader';

/*
 * The role, once per signed-in user per page load. This ran a Firestore read
 * and a remote-config check on EVERY guarded navigation, with a full-viewport
 * spinner in the content pane each time.
 */
const roleCache = new Map();
const roleFor = async (uid) => {
    if (!roleCache.has(uid)) roleCache.set(uid, getUserRole(uid).catch(() => 'user'));
    return roleCache.get(uid);
};

const RouteAccessGuard = ({ children, requiredAccess }) => {
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(true);
    const [hasAccess, setHasAccess] = useState(false);
    const [userRole, setUserRole] = useState(null);
    const [accessConfig, setAccessConfig] = useState(null);
    const user = auth.currentUser;

    useEffect(() => {
        const checkAccess = async () => {
            if (!user) {
                setIsLoading(false);
                return;
            }

            try {
                // Get user role
                const role = await roleFor(user.uid);
                setUserRole(role);

                // Initialize remote config and get access configuration
                const initialized = await initializeRemoteConfig();
                if (initialized) {
                    const config = getRemoteConfigValue('dashboard_access_config') || {};
                    setAccessConfig(config);

                    // RULE 1: If route key exists → check role flag
                    if (requiredAccess && config[requiredAccess]) {
                        setHasAccess(config[requiredAccess][role] === true);
                    }
                    // RULE 2: If route key does NOT exist → allow by default
                    else {
                        setHasAccess(true);
                    }
                } else {
                    setHasAccess(true); // If remote config fails, allow fallback
                }
            } catch (error) {
                console.error('Error checking route access:', error);
                setHasAccess(false);
            } finally {
                setIsLoading(false);
            }
        };

        checkAccess();
    }, [user, requiredAccess]);

    // Inside the app shell, so never fullScreen — see Loader.
    if (isLoading && requiredAccess) {
        return <Loader />;
    }

    // If no specific access is required → allow
    if (!requiredAccess) {
        return children;
    }

    // If access denied → redirect to dashboard/domains (fallback root)
    if (!hasAccess) {
        // console.log(`Redirecting from ${location.pathname} to /dashboard/statistics - insufficient access`);
        return <Navigate to="/dashboard/statistics" replace />;
    }

    return children;
};

export default RouteAccessGuard;
